import React, { useEffect, useRef, useState, forwardRef } from 'react';
import classNames from 'classnames';
import proptypes from 'prop-types';
const { arrayOf, bool, func, number, oneOf, oneOfType, shape, string, } = proptypes;
import { HOTSPOT_TYPE, MAX_DISPLAYED_HOTSPOTS_COUNT } from '../../constants';
import { triggerAnalytics } from '../../helpers';
import { useExperienceContext } from '../../hooks/useExperienceContext';
import { HotspotLensOverlay } from './HotspotLensOverlay';
import { LifeStyleHotspot } from './LifeStyleHotspot';
import { ShowRelatedItems } from './ShowRelatedItems';
import './styles/hotspot.scss';

const LifestyleImageWithHotspot = forwardRef(({
   hotspots, imageUrl, tagIndex, imgOnclick, handleClick, mediaGalleryIndex, type, onShowFirstTagProduct,
}, ref) => {

  const imageRef = useRef(null);
  const [isVertical, setIsVertical] = useState(false);

  const { smModifierEnabled } = useExperienceContext();

  useEffect(() => {
    if (imageRef?.current) {
      const imageBoundingClientRect = imageRef.current.getBoundingClientRect();
      setIsVertical(imageBoundingClientRect.height > imageBoundingClientRect.width);
    }
  }, [imageRef]);

  const onClick = (omsIDs, index, event, collapsedCount) => {
    handleClick(type === HOTSPOT_TYPE.PIP_HOTSPOTS ? true : omsIDs, index, event);

    if (collapsedCount && type === HOTSPOT_TYPE.PIP_HOTSPOTS) {
      triggerAnalytics('product-hotspots.collapsed-hotspot-pip-click', { skus: omsIDs });
    } else if (collapsedCount && type === HOTSPOT_TYPE.DRAWER_HOTSPOTS) {
      triggerAnalytics('product-hotspots.collapsed-hotspot-drawer-click', { skus: omsIDs });
    }
  };

  return (
    <div
      className={
        /** conditional and non-mobile responsive design styles */
        classNames({
            'sui-m-auto': type === HOTSPOT_TYPE.LENS_HOTSPOTS,
            'sui-h-full': type === HOTSPOT_TYPE.LENS_HOTSPOTS && isVertical,
            'sui-w-[90%]': type === HOTSPOT_TYPE.LENS_HOTSPOTS && !isVertical,
            'sm:sui-h-full sm:sui-w-auto': smModifierEnabled && type === HOTSPOT_TYPE.LENS_HOTSPOTS && !isVertical,
          },
          /** universal design styles */
          'product-hotspots',
          'sui-relative',
        )
      }
      data-component="MediaHotspots"
      ref={ref}
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <img
        id="lifestyleimage"
        alt="lifestyleimage"
        className={
          /** conditional and non-mobile responsive design styles */
          classNames({
              'sui-mix-blend-multiply': type === HOTSPOT_TYPE.LENS_HOTSPOTS,
              '!sui-h-auto': type === HOTSPOT_TYPE.LENS_HOTSPOTS && !isVertical,
              'sm:!sui-h-full': smModifierEnabled && type === HOTSPOT_TYPE.LENS_HOTSPOTS && !isVertical,
            },
            /** universal design styles */
            'sui-w-full',
            'sui-h-full',
          )
        }
        src={imageUrl}
        height="1"
        width="1"
        onClick={imgOnclick}
        aria-label="lifestyleImage"
        ref={imageRef}
      />
      {type === HOTSPOT_TYPE.LENS_HOTSPOTS && hotspots[tagIndex]?.boundingBox && (
        <HotspotLensOverlay boundingBox={hotspots[tagIndex].boundingBox} imageUrl={imageUrl} imageRef={imageRef} />
      )}
      {hotspots.length > 0
        && type === HOTSPOT_TYPE.PIP_HOTSPOTS && <ShowRelatedItems showFirstTagProduct={onShowFirstTagProduct} />}
      {hotspots.length > 0 && hotspots.slice(0, MAX_DISPLAYED_HOTSPOTS_COUNT).map((hotspot, index) => {
        const { coordinate, omsIDs, collapsedCount } = hotspot;
        return (
          <div
            id={`drawer-wrapper-div-${index}`}
            key={type === HOTSPOT_TYPE.PIP_HOTSPOTS ? mediaGalleryIndex + '-' + index : index}
          >
            <LifeStyleHotspot
              xCoordinate={parseFloat(coordinate.xCoordinate)}
              yCoordinate={parseFloat(coordinate.yCoordinate)}
              omsIDs={omsIDs}
              index={index}
              currentTagIndex={tagIndex}
              type={type}
              handleClick={(event) => onClick(omsIDs, index, event, collapsedCount)}
              collapsedCount={collapsedCount}
            />
          </div>
        );
      })}
    </div>
  );
});

LifestyleImageWithHotspot.displayName = 'LifestyleImageWithHotspot';

LifestyleImageWithHotspot.propTypes = {
  hotspots: oneOfType([
    arrayOf(
      shape({
        coordinate: shape({
          xCoordinate: string,
          yCoordinate: string
        }),
        omsIDs: arrayOf(string),
        collapsedCount: number
      })
    ),
    arrayOf(
      shape({
        isAnchor: bool,
        boundingBox: shape({
          xMin: string,
          xMax: string,
          yMin: string,
          yMax: string,
        }),
        coordinate: shape({
          xCoordinate: string,
          yCoordinate: string
        }),
        omsIDs: arrayOf(string)
      })
    ),
  ]).isRequired,
  imageUrl: string.isRequired,
  tagIndex: number,
  imgOnclick: func,
  handleClick: func,
  type: oneOf([HOTSPOT_TYPE.PIP_HOTSPOTS, HOTSPOT_TYPE.LENS_HOTSPOTS, HOTSPOT_TYPE.DRAWER_HOTSPOTS]),
  mediaGalleryIndex: number,
  onShowFirstTagProduct: func,
};

LifestyleImageWithHotspot.defaultProps = {
  imgOnclick: null,
  handleClick: null,
  tagIndex: 0,
  type: HOTSPOT_TYPE.PIP_HOTSPOTS,
  mediaGalleryIndex: 0,
  onShowFirstTagProduct: null
};

export { LifestyleImageWithHotspot };
